import { Divider, Stack, ThemeProvider, Typography } from '@mui/material'
import { AppV1AppUserServiceowner } from 'c1gen/roles'
import { C1Chip } from 'c1ui/components/C1Chip'
import { C1Tooltip } from 'c1ui/components/C1Tooltip'
import { C1DarkTheme } from 'c1ui/theme/C1Theme'
import { AppAvatar, AppUserAvatar } from 'components/core/avatar'
import { AvatarSize } from 'components/core/avatar/AvatarSizes'
import { C1LinkButton } from 'components/core/button'
import { C1DisplayNameSkeleton, DisplayNameDetails, DisplayNameDetailsProps } from 'components/core/display-name-details'
import { MinimalAPIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1MultiUserDisplayWithFetch } from 'components/core/multi-user/C1MultiUserDisplay'
import { Metadata, MetadataPropType } from 'components/core/object-display/C1ObjectDisplay'
import { MetadataKeyValuePair } from 'components/core/page-header'
import { C1ScrollBox } from 'components/core/scrollbox/C1ScrollBox'
import { VariableText } from 'components/core/variable-text'
import { useHasRole, useHasBuiltinRole } from 'components/no-ui/capability'
import { removeDoubleQuotes } from 'components/no-ui/string-formatting'
import { getAppUserProfileFieldValue } from 'components/pages/app/users/details'
import { AppUserStatus } from 'components/pages/app/users/status/AppUserStatus'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { RoleID } from 'lib/constants/system'
import { routes, Route } from 'lib/routes'
import { ExternalLink, Globe } from 'lucide-react'
import { App } from 'pbts/c1/api/app/v1/app'
import { AppUserServiceGetRequest, AppUserStatus_Status, AppUserType } from 'pbts/c1/api/app/v1/app_user'
import { Value } from 'pbts/google/protobuf/struct'
import { FC, ReactNode, useMemo } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { getAppUserApi } from 'redux_toolkit/app-users'

export type AppUserDisplayNameProps = {
    appUser: AppUserValue | undefined
    app?: App | undefined
    avatarSize?: AvatarSize
    includeDetails?: boolean
    showTooltip?: boolean
    showDeletedDisabledStatus?: boolean
    showAllUserStatus?: boolean
    iconOnly?: boolean
    showAvatar?: boolean
} & Pick<DisplayNameDetailsProps, 'padded' | 'margin' | 'readOnly' | 'displayNameVariant'>

type AppUserTooltipContentProps = {
    appUser: AppUserValue
}

export const AppUserTooltipContent: FC<AppUserTooltipContentProps> = ({ appUser }) => {
    const intl = useIntl()
    const link = routes.applications.account(appUser.appId, appUser.id)

    return (
        <ThemeProvider theme={C1DarkTheme}>
            <Stack gap={1} py={1}>
                <Stack direction="row" gap={2} mb={1}>
                    <Typography variant="h4">{appUser.displayName}</Typography>
                    <AppUserStatus appUser={appUser} />
                    {appUser.isExternal && <C1Chip
                        color="primary"
                        icon={<C1Icon icon={Globe} size="sm" sx={{ color: 'primary.main' }} />}
                        label={<FormattedMessage id="component_app_user_display_name_is_external" defaultMessage="External" />}
                    />}
                </Stack>
                <MetadataKeyValuePair
                    metadataKey={intl.formatMessage({
                        id: 'component_app_user_display_name_email',
                        defaultMessage: 'Email',
                    })}
                    metadataValue={<VariableText variableText={appUser.email} />}
                    isCopyable
                    copyValue={appUser.email}
                />

                {(appUser.identityUser || appUser.identityUserId) && <MetadataKeyValuePair
                    metadataKey={intl.formatMessage({
                        id: 'component_app_user_display_name_account_owner',
                        defaultMessage: 'Account owner',
                    })}
                    metadataValue={
                        appUser.identityUser
                            ? <UserDisplayName
                                user={appUser.identityUser}
                                includeDetails={false}
                                showTooltip={false}
                                readOnly
                                margin={false}
                            />
                            : <C1MultiUserDisplayWithFetch
                                userIds={[appUser.identityUserId]}
                                readOnly
                                showTooltip={false}
                                margin={false}
                            />
                    }
                    fullWidth
                />}

                <MetadataKeyValuePair
                    metadataKey={intl.formatMessage({
                        id: 'component_app_user_display_name_type',
                        defaultMessage: 'Type',
                    })}
                    metadataValue={<VariableText variableText={getAppUserTypeString(appUser.appUserType, intl)} />}
                    isCopyable
                    copyValue={getAppUserTypeString(appUser.appUserType, intl)}
                    fullWidth
                />

                {appUser.profile && Object.entries(appUser.profile.fields).length > 0 && <>
                    <Divider sx={{ mt: 2, borderColor: 'divider' }} />
                    <C1ScrollBox>
                        <Typography variant="h5" mb={1}>
                            <FormattedMessage id="component_app_user_display_name_profile" defaultMessage="Profile attributes" />
                        </Typography>

                        {appUser.profile && Object.entries(appUser.profile.fields).map(([key, value]) => (
                            <MetadataKeyValuePair
                                key={key}
                                metadataKey={<Typography variant="monospace">{key}</Typography>}
                                metadataValue={removeDoubleQuotes(Value.toJsonString(value, { prettySpaces: 2 }))}
                                isCopyable
                                copyValue={removeDoubleQuotes(Value.toJsonString(value, { prettySpaces: 2 }))}
                                fullWidth
                            />
                        ))}
                    </C1ScrollBox>
                </>}

                <Divider sx={{ mb: 1, borderColor: 'divider' }} />

                <C1LinkButton
                    href={link}
                    target="_blank"
                    color="info"
                    variant="text"
                    endIcon={<C1Icon icon={ExternalLink} size="sm" />}
                >
                    <FormattedMessage id="component_app_user_display_name_edit_profile" defaultMessage="View account detail page" />
                </C1LinkButton>
            </Stack>
        </ThemeProvider>
    )
}

export const urlFromAppUser = (appUser: AppUserValue): Route =>
    routes.applications.account(appUser.appId, appUser.id)
export const AppUserDisplayName = ({
    appUser,
    app,
    avatarSize,
    showAvatar = true,
    readOnly = false,
    displayNameVariant,
    includeDetails = true,
    padded = false,
    showDeletedDisabledStatus = false,
    showAllUserStatus = false,
    margin = false,
    iconOnly = false,
    showTooltip: _showTooltip = false,
}: AppUserDisplayNameProps): React.ReactElement | null => {
    const isReadOnlySuperAdmin = useHasBuiltinRole(RoleID.ReadOnlySuperAdmin)
    const hasRole = useHasRole(AppV1AppUserServiceowner)
    const canNavigateToAppUser = isReadOnlySuperAdmin || hasRole
    const showTooltip = _showTooltip && hasRole && !appUser?.deletedAt

    const appUserStatus = useMemo(() => {
        if (showAllUserStatus) {
            return <AppUserStatus appUser={appUser} />
        }
        if (showDeletedDisabledStatus &&
            (appUser?.status?.status === AppUserStatus_Status.DISABLED || appUser?.status?.status === AppUserStatus_Status.DELETED)) {
            return <AppUserStatus appUser={appUser} />
        }
        return undefined
    }, [showAllUserStatus, showDeletedDisabledStatus, appUser])

    if (!appUser) {
        return null
    }

    const link = (readOnly || !canNavigateToAppUser) ? undefined : urlFromAppUser(appUser)
    const avatar = showAvatar ? <AppUserAvatar
        displayName={appUser.displayName}
        size={avatarSize}
        appUserType={appUser.appUserType}
        avatarBadge={<AppAvatar appId={appUser.appId} size="xs" sx={{ backgroundColor: 'background.default' }} decorative />}
        email={appUser.email}
        decorative={!iconOnly}
        identityUserType={appUser.identityUser?.type}
    /> : undefined

    const tooltipContent = <AppUserTooltipContent appUser={appUser} />

    return (
        <DisplayNameDetails
            avatar={avatar}
            displayName={!iconOnly ? appUser.displayName : ''}
            link={link}
            details={<AppUserNameMetadata
                appUser={appUser}
                includeDetails={includeDetails}
                iconOnly={iconOnly}
                showTooltip={showTooltip}
                app={app}
            />}
            readOnly={readOnly || appUser.deletedAt !== undefined || !canNavigateToAppUser}
            displayNameVariant={displayNameVariant}
            tooltipContent={tooltipContent}
            showTooltip={showTooltip}
            padded={padded}
            margin={margin}
            isDeleted={appUser.deletedAt !== undefined}
            status={<Stack direction="row" gap={1} alignItems="center">
                {appUserStatus}
                {!includeDetails && <ExternalIcon appUser={appUser} showTooltip={!showTooltip} size="md" />}
            </Stack>}
            tooltipProps={{
                PopperProps: {
                    sx: {
                        '& .MuiTooltip-tooltip': { maxWidth: '400px' },
                    },
                },
                slotProps: {},
            }}
            actionName="View app user"
        />
    )
}

export const getAppUserTypeString = (v: AppUserType, intl: IntlShape): string => {
    switch (v) {
        case AppUserType.USER:
            return intl.formatMessage({ id: 'exclusion_picker_app_user_type_users', defaultMessage: 'User' })
        case AppUserType.SERVICE_ACCOUNT:
            return intl.formatMessage({
                id: 'exclusion_picker_app_user_type_service_accounts',
                defaultMessage: 'Service account',
            })
        case AppUserType.SYSTEM_ACCOUNT:
            return intl.formatMessage({
                id: 'exclusion_picker_app_user_type_system_accounts',
                defaultMessage: 'System account',
            })
        case AppUserType.UNSPECIFIED: // fallthrough
        default:
            return intl.formatMessage({
                id: 'exclusion_picker_app_user_type_unspecified',
                defaultMessage: 'Unspecified',
            })
    }
}

const ExternalIcon: FC<{ appUser: AppUserValue, showTooltip: boolean, size: 'sm' | 'md' | 'lg' }>
    = ({ appUser, showTooltip, size }) => {
        if (!appUser.isExternal) {
            return null
        }
        if (showTooltip) {
            return <C1Tooltip title={<FormattedMessage id="component_app_user_display_name_external_account" defaultMessage="External account" />}>
                <C1Icon icon={Globe} size={size} sx={{ color: 'primary.main' }} />
            </C1Tooltip>
        }
        return <C1Icon icon={Globe} size={size} sx={{ color: 'primary.main' }} />
    }

const AppUserNameMetadata: FC<{
    appUser: AppUserValue
    app?: App
    includeDetails: boolean
    iconOnly: boolean
    showTooltip: boolean
}> = ({ appUser, includeDetails, iconOnly, showTooltip }): React.ReactElement | null => {
    const intl = useIntl()

    const aws_user_type = getAppUserProfileFieldValue(appUser, 'aws_user_type').toUpperCase()
    const appUserTypeString = getAppUserTypeString(appUser.appUserType, intl)

    const metadata = useMemo(() => {
        const result: MetadataPropType = []

        if (appUser.email && result.length < 2) {
            result.push({
                text: appUser.email,
                // show the external icon tooltip if the main account tooltip is not shown
                icon: <ExternalIcon appUser={appUser} showTooltip={!showTooltip} size="sm" />,
            })
        }
        // In the case of AWS users, we want AWS user type take precedence.
        if (aws_user_type && result.length < 2) result.push({ text: aws_user_type })
        if (appUserTypeString && result.length < 2) result.push({ text: appUserTypeString })

        return result
    }, [appUser, appUserTypeString, aws_user_type, showTooltip])

    if (iconOnly || !includeDetails) {
        return null
    }

    return <Metadata metadata={metadata} />
}
// Fetches the app user and displays the app user display name.
type AppUserDisplayNameWithFetchProps = {
    appId: string
    id: string
} & Omit<AppUserDisplayNameProps, 'appUser'>

export const AppUserDisplayNameWithFetch = ({ appId, id, ...rest }: AppUserDisplayNameWithFetchProps): ReactNode => {
    const {
        data: appUser,
        isLoading: appUserLoading,
        isError: appUserError,
        error: appUserErrorData,
    } = getAppUserApi.useGet(AppUserServiceGetRequest.create({
        id,
        appId,
        expandMask: { paths: ['*'] },
    }))

    if (appUserError && appUserErrorData) {
        return <MinimalAPIError
            err={<FormattedMessage id="display_app_user_error" defaultMessage="Error fetching user" />}
        />
    }

    if (!appUser) {
        return appUserLoading ? <C1DisplayNameSkeleton /> : null
    }

    return <AppUserDisplayName appUser={appUser} {...rest} />
}
